import { useCallback, useLayoutEffect, useMemo } from 'react'
import { useGetUserSession, useLogin } from '@/shared/hooks/api/auth'
import i18n from 'i18next'
import { Loader2 } from 'lucide-react'
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom'
import { Toaster } from '@/components/ui/sonner'
import { SessionContext } from '@/shared/hooks/useSession'
import { SignUp } from '@/features/auth/sign-up/sign-up'
import { SignIn } from '@/features/auth/sign-in/sign-in'
import { ForgotPasswordPage } from '@/features/auth/forgot-password-page'
import { ResetPasswordPage } from '@/features/auth/reset-password-page'
import { ChangePasswordPage } from '@/features/auth/change-password-page'
import { OnboardingFlow } from '@/features/onboarding/onboarding-flow'
import { SUPPORTED_LANGUAGES } from '@/shared/config/languages'
import AuthenticatedApp from '@/app/AuthenticatedApp'

const PUBLIC_UNAUTH_PATH_PREFIXES = [
  '/sign-in',
  '/signup',
  '/sign-up',
  '/forgot-password',
  '/reset-password',
] as const

function isPublicUnauthenticatedPath(pathname: string): boolean {
  return PUBLIC_UNAUTH_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  )
}

function safeRedirectPath(redirect: string | null): string {
  if (!redirect) {
    return '/'
  }
  if (redirect.startsWith('http://') || redirect.startsWith('https://')) {
    return '/'
  }
  if (!redirect.startsWith('/') || redirect.startsWith('//')) {
    return '/'
  }
  return redirect
}

function signInPathForReturnTo(returnTo: string): string {
  if (returnTo === '/') {
    return '/sign-in'
  }
  return `/sign-in?redirect=${encodeURIComponent(returnTo)}`
}

function LoadingScreen() {
  return (
    <div className="flex min-h-svh items-center justify-center">
      <Loader2 className="size-8 animate-spin text-muted-foreground" />
    </div>
  )
}

function normalizeSessionLanguage(lng: string): string | null {
  const supported = SUPPORTED_LANGUAGES.map((item) => item.value)
  if (supported.includes(lng as (typeof supported)[number])) {
    return lng
  }
  if (lng === 'en') {
    return 'en-GB'
  }
  return null
}

export default function Root() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const postSignInPath = useMemo(
    () => safeRedirectPath(searchParams.get('redirect')),
    [searchParams]
  )
  const { data: session, refetch: refetchSession, isFetched: isAuthCheckComplete } =
    useGetUserSession()
  const { mutateAsync: login, isPending: isSigningIn } = useLogin()

  const isAuthenticated = session != null

  const handleSignIn = useCallback(
    async (payload: { email: string; password: string }) => {
      await login(payload)
      await refetchSession()
      navigate(postSignInPath, { replace: true })
    },
    [login, refetchSession, navigate, postSignInPath]
  )

  useLayoutEffect(() => {
    const mapped = session?.language
      ? normalizeSessionLanguage(session.language)
      : null
    if (mapped) {
      void i18n.changeLanguage(mapped)
    }
  }, [session?.language])

  if (!isAuthCheckComplete) {
    return <LoadingScreen />
  }

  if (!isAuthenticated) {
    if (location.pathname === '/sign-up') {
      return <Navigate to="/signup" replace />
    }
    if (!isPublicUnauthenticatedPath(location.pathname)) {
      const returnTo = `${location.pathname}${location.search}${location.hash}`
      return <Navigate to={signInPathForReturnTo(returnTo)} replace />
    }
    return (
      <>
        <Routes>
          <Route path="/sign-in" element={<SignIn onSignIn={handleSignIn} isSigningIn={isSigningIn} />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="*" element={<Navigate to="/sign-in" replace />} />
        </Routes>
        <Toaster duration={5000} />
      </>
    )
  }

  if (session.passwordResetRequired) {
    return (
      <>
        <Routes>
          <Route path="*" element={<ChangePasswordPage />} />
        </Routes>
        <Toaster duration={5000} />
      </>
    )
  }

  const isVerified = session.organization.activatedAt != null
  const isMandatoryInfoComplete = session.organization.isMandatoryInfoComplete
  const needsOnboarding = !isVerified || !isMandatoryInfoComplete

  if (needsOnboarding) {
    return (
      <SessionContext.Provider value={{ session }}>
        <>
          <Routes>
            <Route path="/onboarding/*" element={<OnboardingFlow />} />
            <Route path="*" element={<Navigate to="/onboarding" replace />} />
          </Routes>
          <Toaster duration={5000} />
        </>
      </SessionContext.Provider>
    )
  }

  return (
    <SessionContext.Provider value={{ session }}>
      <>
        <AuthenticatedApp />
        <Toaster duration={5000} />
      </>
    </SessionContext.Provider>
  )
}
